// Runtime environment detection so the app can auto-adapt its controls + UI to
// touch devices (phones/tablets) without a build-time flag. Kept tiny and
// dependency-free; called once at startup (see App constructor).

/**
 * True when the PRIMARY pointer is a finger — a real phone/tablet — as opposed to
 * a desktop with a mouse (even a touchscreen laptop, which reports a fine primary
 * pointer). We require BOTH a coarse primary pointer AND touch capability so a
 * mouse-driven touchscreen still gets the full desktop control scheme.
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  const hasTouch = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
  return coarse && hasTouch;
}

/** Tag <body> with `touch`/`desktop` so CSS (and any query) can branch. Returns isTouch. */
export function applyEnvClass(): boolean {
  const touch = isTouchDevice();
  document.body.classList.toggle('touch', touch);
  document.body.classList.toggle('desktop', !touch);
  return touch;
}

/**
 * Stop the browser's edge-swipe back/forward navigation gesture from firing
 * mid-game — a drag-to-aim game routinely starts drags near the screen edge,
 * and on a phone that reads to the browser chrome as "go back", which quits
 * the match. `overscroll-behavior` (see styles.css) covers this in Chrome for
 * Android; iOS Safari's edge swipe is a system gesture with no CSS switch, so
 * we fall back to the standard workaround: cancel touchmove once a touch has
 * started within `edgePx` of either edge, before the gesture can complete.
 */
export function preventEdgeSwipeNavigation(edgePx = 24): void {
  if (typeof document === 'undefined') return;
  let active = false;
  document.addEventListener(
    'touchstart',
    (e) => {
      const t = e.touches[0];
      active = !!t && (t.clientX < edgePx || t.clientX > window.innerWidth - edgePx);
    },
    { passive: true }
  );
  document.addEventListener(
    'touchmove',
    (e) => {
      if (active) e.preventDefault();
    },
    { passive: false }
  );
}

/**
 * True on macOS. Used to let ⌘ (Meta) stand in for Ctrl on keyboard shortcuts —
 * Mac keyboards put Ctrl in an awkward corner and everyone's muscle memory is ⌘
 * for "the modifier key", so a Mac player holding ⌘ instead of Ctrl should get
 * the same result, not a dead shortcut.
 */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  // `userAgentData.platform` is the modern source; `platform`/`userAgent` cover
  // older/other browsers. Any of them naming Mac is good enough here.
  const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  const platform = uaData?.platform ?? navigator.platform ?? '';
  return /mac/i.test(platform) || /mac/i.test(navigator.userAgent);
}
